import { Injectable } from '@angular/core';
import { DropdownItem, HighchartsThemeService } from '@frk/eds-components';
import { ToggleOption } from '@types';
import { symbols } from '@utils/il8n/currency';
import { SeriesOptionsType } from 'highcharts';
import { Subject, Observable } from 'rxjs';
import { InteractiveContentTranslateService } from '../../interactive-content-translate.service';
import {
  GermanCalculationType,
  GermanCalculatorChartData,
  GermanCalculatorInstallmentPeriod,
  GermanCalculatorPerpetualAnnuity,
  GermanCalculatorResultType,
  GermanCalculatorState,
  GermanCalculatorSummaryData,
  GermanCalculatorSummaryDataValueType,
  GermanCalculatorTableData,
  GermanCalculatorType,
  GermanPensionAdjustments,
  GermanPensionCalculatorAnnualPensionAdjustment,
  GermanPensionCalculatorConsiderInflation,
  GermanPensionCalculatorEmployedInEast,
  GermanPensionCalculatorGender,
  GermanPensionCalculatorKnowPayPoints,
} from '../types/german-calculator.type';
import {
  GermanCalculatorResultPeriod,
  GermanCalculatorResultSummary,
} from '../types/model/german-calculator';
import { PagedRecords } from '@utils/paging/paging-utils';
import {
  RETIREMENT_AGE_MONTHS,
  RETIREMENT_AGE_YEARS,
} from './german-calculator.config';

// ******************** CONFIGURATION ********************

// Add columns to be present in result summary in appropriate order.
// Column name should exactly be same as present in field name in 'GermanCalculatorResultSummary',
const SUMMARY_ROW_LIST: Record<
  GermanCalculatorType,
  (keyof GermanCalculatorResultSummary)[]
> = {
  0: [
    'investmentPeriod',
    'interestRate',
    'finalBalance',
    'initialBalance',
    'interestIncome',
  ],
  1: [
    'investmentPeriod',
    'installmentAmount',
    'interestRate',
    'finalBalance',
    'initialBalance',
    'totalDeposits',
    'interestIncome',
  ],
  2: [
    'withdrawlAmount',
    'totalDeposits',
    'investmentPeriod',
    'interestRate',
    'initialBalance',
    'interestIncome',
  ],
  3: [], // Intentionally left blank, not needed in the same type.
  4: [],
  5: [],
};

// Add columns to be present in result table in appropriate order.
// Column name should exactly be same as present in field name in 'GermanCalculatorResultPeriod',
// Alongwith 'year', 'cumulativeInterest' and 'cumulativeDeposit'.
type GermanCalculatorResultPeriodList = GermanCalculatorResultPeriod & {
  year?: number; // To be the first field.
  cumulativeInterest?: number; // One time investment field.
  cumulativeDeposit?: number; // Savings plan field.
};
const TABLE_COL_LIST: Record<
  GermanCalculatorType,
  (keyof GermanCalculatorResultPeriodList)[]
> = {
  0: [
    'year',
    'initialBalance',
    'interestIncome',
    'cumulativeInterest',
    'finalBalance',
  ],
  1: [
    'year',
    'interestIncome',
    'deposits',
    'cumulativeInterestIncome',
    'cumulativeDeposit',
    'finalBalance',
  ],
  2: ['year', 'initialBalance', 'interestIncome', 'withdrawls', 'finalBalance'],
  3: [], // Intentionally left blank, not needed in the same type.
  4: [],
  5: [],
};

// Define the number of legend rows for each calculator type.
// To be used later, for defining summary legend colors with respect to chart.
const SUMMARY_LEGEND_ROW_COUNT: Record<GermanCalculatorType, number> = {
  0: 2,
  1: 3,
  2: 2,
  3: 0, // Intentionally assigned 0, not needed in the same type.
  4: 0,
  5: 0,
};

// Limit the enum 'GermanCalculationType' keys as per calculator type.
const CALCULATION_TYPES_FOR_ONE_TIME_INVESTMENT: (keyof typeof GermanCalculationType)[] = [
  'INITIAL_CAPITAL',
  'TERM',
  'INTEREST_RATE',
  'END_VALUE',
];
const CALCULATION_TYPES_FOR_SAVINGS_PLAN: (keyof typeof GermanCalculationType)[] = [
  'INITIAL_CAPITAL',
  'SAVINGS_RATE',
  'TERM',
  'INTEREST_RATE',
  'END_VALUE',
];
const CALCULATION_TYPES_FOR_WITHDRAWL_PLAN: (keyof typeof GermanCalculationType)[] = [
  'INITIAL_CAPITAL',
  'PENSION',
  'INTEREST_RATE',
  'TERM',
];

@Injectable({
  providedIn: 'root',
})
export class GermanCalculatorBaseService {
  protected state: GermanCalculatorState;
  protected gcOneTimeInvestmentState$: Subject<GermanCalculatorState>;
  private currencySymbol: string;
  private pagedRecords: PagedRecords<string[]>;

  // Duration labels.
  protected brLabels: object;
  protected yearLabel: string;
  protected yearsLabel: string;
  protected dayLabel: string;
  protected daysLabel: string;
  protected monthLabel: string;
  protected monthsLabel: string;

  constructor(
    protected icTranslateService: InteractiveContentTranslateService,
    protected highchartsThemeService: HighchartsThemeService
  ) {
    this.gcOneTimeInvestmentState$ = new Subject();
    this.currencySymbol = symbols.get('EUR');
    this.pagedRecords = new PagedRecords<string[]>(10); // Page size set to '10' (Ref. legacy code).
  }

  /**
   * Return the calculator state subject as observable.
   */
  getGcOneTimeInvestmentState$(): Observable<GermanCalculatorState> {
    return this.gcOneTimeInvestmentState$.asObservable();
  }

  /**
   * Return the currency symbol to used in calculators.
   */
  getCurrencySymbol(): string {
    return this.currencySymbol;
  }

  /**
   * Return the error message in case API request fails.
   */
  getAPIFailMessage(): string {
    return this.state.config.formValues.invalidLabelValues.apiErrorMessage;
  }

  /**
   * Assign duration labels to be used in other methods.
   */
  protected assignDurationLabels(): void {
    // Assign resuable year/month/day (s) label.
    this.yearLabel = this.icTranslateService.instant(
      this.brLabels['gc-year'],
      'gc-year'
    );
    this.yearsLabel = this.icTranslateService.instant(
      this.brLabels['gc-years'],
      'gc-years'
    );
    this.dayLabel = this.icTranslateService.instant(
      this.brLabels['gc-day'],
      'gc-day'
    );
    this.daysLabel = this.icTranslateService.instant(
      this.brLabels['gc-days'],
      'gc-days'
    );
    this.monthLabel = this.icTranslateService.instant(
      this.brLabels['gc-month'],
      'gc-month'
    );
    this.monthsLabel = this.icTranslateService.instant(
      this.brLabels['gc-months'],
      'gc-months'
    );
  }

  /**
   * Assign invalid text labels to be used in form fields & result section.
   */
  protected assignInvalidLabelValuesToState(): void {
    this.state.config.formValues.invalidLabelValues = {
      initialSalesChargeInvalidText: this.icTranslateService.instant(
        this.brLabels['gc-invalid-label-initial-sales-charge'],
        'gc-invalid-label-initial-sales-charge'
      ),
      interestRateInvalidText: this.icTranslateService.instant(
        this.brLabels['gc-invalid-label-interest-rate'],
        'gc-invalid-label-interest-rate'
      ),
      noResultErrorMessage: this.icTranslateService.instant(
        this.brLabels['gc-api-no-result-message'],
        'gc-api-no-result-message'
      ),
      apiErrorMessage: this.icTranslateService.instant(
        this.brLabels['gc-api-fail-message'],
        'gc-api-fail-message'
      ),
    };
  }

  /**
   * Extract calculation types from its enum as per calculator type.
   * Transform into dropdown items and return it.
   * @param calculatorType calculator type like one time/savings/withdrawl
   */
  protected getCalculationTypeItems(
    calculatorType: GermanCalculatorType
  ): DropdownItem[] {
    let expectedCalculationTypes: (keyof typeof GermanCalculationType)[];
    const calculationTypeItems: DropdownItem[] = [];

    switch (calculatorType) {
      case GermanCalculatorType.ONE_TIME_INVESTMENT:
        expectedCalculationTypes = CALCULATION_TYPES_FOR_ONE_TIME_INVESTMENT;
        break;
      case GermanCalculatorType.SAVINGS_PLAN:
        expectedCalculationTypes = CALCULATION_TYPES_FOR_SAVINGS_PLAN;
        break;
      case GermanCalculatorType.WITHDRAWL_PLAN:
        expectedCalculationTypes = CALCULATION_TYPES_FOR_WITHDRAWL_PLAN;
        break;
    }

    expectedCalculationTypes.forEach((calculationType) => {
      calculationTypeItems.push({
        label: this.icTranslateService.instant(
          this.brLabels[`gc-calculation-type-${calculationType}`],
          `gc-calculation-type-${calculationType}`
        ),
        value: calculationType,
      });
    });

    return calculationTypeItems;
  }

  /**
   * Extract installment periods from its enum.
   * Transform into dropdown items and return it.
   */
  protected getInstallmentPeriodItems(): DropdownItem[] {
    const installmentPeriodItems: DropdownItem[] = [];

    Object.keys(GermanCalculatorInstallmentPeriod).forEach((key) => {
      installmentPeriodItems.push({
        label: this.icTranslateService.instant(
          this.brLabels[`gc-installment-period-${key}`],
          `gc-installment-period-${key}`
        ),
        value: key,
      });
    });

    return installmentPeriodItems;
  }

  /**
   * Get numeric code as string for corresponding installment period.
   * @param defaultInstallmentPeriod default intallment period selected
   */
  protected getNumeralCodeForInstallmentPeriod(
    defaultInstallmentPeriod: GermanCalculatorInstallmentPeriod
  ): string {
    return String(
      Object.keys(GermanCalculatorInstallmentPeriod).findIndex(
        (installmentPeriod) => installmentPeriod === defaultInstallmentPeriod
      ) + 1
    );
  }

  /**
   * Extract perpetual annuity from its enum.
   * Transform into dropdown items and return it.
   */
  protected getPerpetualAnnuityItems(): DropdownItem[] {
    const perpetualAnnuityItems: DropdownItem[] = [];

    Object.keys(GermanCalculatorPerpetualAnnuity).forEach((key) => {
      perpetualAnnuityItems.push({
        label: this.icTranslateService.instant(
          this.brLabels[`gc-perpetual-annuity-${key}`],
          `gc-perpetual-annuity-${key}`
        ),
        value: key,
      });
    });

    return perpetualAnnuityItems;
  }

  /**
   * Extract payout from its enum.
   * Transform into dropdown items and return it.
   */
  protected getPayoutItems(): DropdownItem[] {
    const payoutItems: DropdownItem[] = [];

    Object.keys(GermanCalculatorInstallmentPeriod).forEach((key) => {
      payoutItems.push({
        label: this.icTranslateService.instant(
          this.brLabels[`gc-payout-${key}`],
          `gc-payout-${key}`
        ),
        value: key,
      });
    });

    return payoutItems;
  }

  /**
   * Transform RETIREMENT_AGE_YEARS to dropdown items and return it.
   */
  protected getRetirementYears(): DropdownItem[] {
    const retirementItems: DropdownItem[] = [];

    RETIREMENT_AGE_YEARS.forEach((key) => {
      retirementItems.push({
        label: String(key),
        value: key,
      });
    });

    return retirementItems;
  }

  /**
   * Transform RETIREMENT_AGE_MONTHS to dropdown items and return it.
   */
  protected getRetirementMonths(): DropdownItem[] {
    const retirementMonths: DropdownItem[] = [];

    RETIREMENT_AGE_MONTHS.forEach((key) => {
      retirementMonths.push({
        label: String(key),
        value: key,
      });
    });
    return retirementMonths;
  }

  /**
   * Return the toggle list for result options.
   * Currently, two options are - chart and table.
   */
  getResultTypeToggleList(): ToggleOption[] {
    return [
      {
        active:
          this.state.config.defaultResultType ===
          GermanCalculatorResultType.CHART,
        text: this.icTranslateService.instant(
          this.brLabels['gc-result-toggle-chart'],
          'gc-result-toggle-chart'
        ),
        value: GermanCalculatorResultType[0],
      },
      {
        active:
          this.state.config.defaultResultType ===
          GermanCalculatorResultType.TABLE,
        text: this.icTranslateService.instant(
          this.brLabels['gc-result-toggle-table'],
          'gc-result-toggle-table'
        ),
        value: GermanCalculatorResultType[1],
      },
    ];
  }

  /**
   * Return the toggle list for gender.
   */
  getGenderToggleList(): ToggleOption[] {
    return [
      {
        active:
          this.state.pensionConfig.formValues.bindValues.gender ===
          GermanPensionCalculatorGender.MALE,
        text: this.icTranslateService.instant(
          this.brLabels[
            `gc-pension-calculator-gender-${GermanPensionCalculatorGender.MALE}`
          ],
          `gc-pension-calculator-gender-${GermanPensionCalculatorGender.MALE}`
        ),
        value: GermanPensionCalculatorGender.MALE,
      },
      {
        active:
          this.state.pensionConfig.formValues.bindValues.gender ===
          GermanPensionCalculatorGender.FEMALE,
        text: this.icTranslateService.instant(
          this.brLabels[
            `gc-pension-calculator-gender-${GermanPensionCalculatorGender.FEMALE}`
          ],
          `gc-pension-calculator-gender-${GermanPensionCalculatorGender.FEMALE}`
        ),
        value: GermanPensionCalculatorGender.FEMALE,
      },
    ];
  }

  /**
   * Return the toggle list for Pension Adjustment.
   */
  getPensionAdjustmentToggleList(): ToggleOption[] {
    return [
      {
        active:
          this.state.pensionConfig.formValues.bindValues.pensionAdjustments ===
          GermanPensionAdjustments.NONE,
        text: this.icTranslateService.instant(
          this.brLabels[
            `gc-pension-calculator-pension-adjustment-${GermanPensionAdjustments.NONE}`
          ],
          `gc-pension-calculator-pension-adjustment-${GermanPensionAdjustments.NONE}`
        ),
        value: String(GermanPensionAdjustments.NONE),
      },
      {
        active:
          this.state.pensionConfig.formValues.bindValues.pensionAdjustments ===
          GermanPensionAdjustments.PERCENT_1,
        text: this.icTranslateService.instant(
          this.brLabels[
            `gc-pension-calculator-pension-adjustment-${GermanPensionAdjustments.PERCENT_1}`
          ],
          `gc-pension-calculator-pension-adjustment-${GermanPensionAdjustments.PERCENT_1}`
        ),
        value: String(GermanPensionAdjustments.PERCENT_1),
      },
      {
        active:
          this.state.pensionConfig.formValues.bindValues.pensionAdjustments ===
          GermanPensionAdjustments.PERCENT_2,
        text: this.icTranslateService.instant(
          this.brLabels[
            `gc-pension-calculator-pension-adjustment-${GermanPensionAdjustments.PERCENT_2}`
          ],
          `gc-pension-calculator-pension-adjustment-${GermanPensionAdjustments.PERCENT_2}`
        ),
        value: String(GermanPensionAdjustments.PERCENT_2),
      },
    ];
  }

  /**
   * Return the toggle list for Emloyed in East.
   */
  getEmployedInEastToggleList(): ToggleOption[] {
    return [
      {
        active:
          this.state.pensionConfig.formValues.bindValues.employedInEast ===
          GermanPensionCalculatorEmployedInEast.OLD,
        text: this.icTranslateService.instant(
          this.brLabels[
            `gc-pension-calculator-empineast-${GermanPensionCalculatorEmployedInEast.OLD}`
          ],
          `gc-pension-calculator-empineast-${GermanPensionCalculatorEmployedInEast.OLD}`
        ),
        value: GermanPensionCalculatorEmployedInEast.OLD,
      },
      {
        active:
          this.state.pensionConfig.formValues.bindValues.employedInEast ===
          GermanPensionCalculatorEmployedInEast.NEW,
        text: this.icTranslateService.instant(
          this.brLabels[
            `gc-pension-calculator-empineast-${GermanPensionCalculatorEmployedInEast.NEW}`
          ],
          `gc-pension-calculator-empineast-${GermanPensionCalculatorEmployedInEast.NEW}`
        ),
        value: GermanPensionCalculatorEmployedInEast.NEW,
      },
    ];
  }

  /**
   * Return the toggle list for Knows Pay Points.
   */
  getKnowsPayPointsToggleList(): ToggleOption[] {
    return [
      {
        active:
          this.state.pensionConfig.formValues.bindValues.knowPayPoints ===
          GermanPensionCalculatorKnowPayPoints.YES,
        text: this.icTranslateService.instant(
          this.brLabels[
            `gc-pension-calculator-know-pay-points-${GermanPensionCalculatorKnowPayPoints.YES}`
          ],
          `gc-pension-calculator-know-pay-points-${GermanPensionCalculatorKnowPayPoints.YES}`
        ),
        value: GermanPensionCalculatorKnowPayPoints.YES,
      },
      {
        active:
          this.state.pensionConfig.formValues.bindValues.knowPayPoints ===
          GermanPensionCalculatorKnowPayPoints.NO,
        text: this.icTranslateService.instant(
          this.brLabels[
            `gc-pension-calculator-know-pay-points-${GermanPensionCalculatorKnowPayPoints.NO}`
          ],
          `gc-pension-calculator-know-pay-points-${GermanPensionCalculatorKnowPayPoints.NO}`
        ),
        value: GermanPensionCalculatorKnowPayPoints.NO,
      },
    ];
  }

  /**
   * Return the toggle list for Knows Pay Points.
   */
  getAnnualPensionAdjustmentToggleList(): ToggleOption[] {
    return [
      {
        active:
          this.state.pensionConfig.formValues.bindValues
            .annualPensionAdjustment ===
          GermanPensionCalculatorAnnualPensionAdjustment.YES,
        text: this.icTranslateService.instant(
          this.brLabels[
            `gc-pension-calculator-annual-pension-adjustment-${GermanPensionCalculatorAnnualPensionAdjustment.YES}`
          ],
          `gc-pension-calculator-annual-pension-adjustment-${GermanPensionCalculatorAnnualPensionAdjustment.YES}`
        ),
        value: GermanPensionCalculatorAnnualPensionAdjustment.YES,
      },
      {
        active:
          this.state.pensionConfig.formValues.bindValues
            .annualPensionAdjustment ===
          GermanPensionCalculatorAnnualPensionAdjustment.NO,
        text: this.icTranslateService.instant(
          this.brLabels[
            `gc-pension-calculator-annual-pension-adjustment-${GermanPensionCalculatorAnnualPensionAdjustment.NO}`
          ],
          `gc-pension-calculator-annual-pension-adjustment-${GermanPensionCalculatorAnnualPensionAdjustment.NO}`
        ),
        value: GermanPensionCalculatorAnnualPensionAdjustment.NO,
      },
    ];
  }

  /**
   * Return the toggle list for CONSIDER INFLATION
   */
  considerInflationToggleList(): ToggleOption[] {
    return [
      {
        active:
          this.state.pensionConfig.formValues.bindValues.considerInflation ===
          GermanPensionCalculatorConsiderInflation.YES,
        text: this.icTranslateService.instant(
          this.brLabels[
            `gc-pension-calculator-consider-inflation-${GermanPensionCalculatorConsiderInflation.YES}`
          ],
          `gc-pension-calculator-consider-inflation-${GermanPensionCalculatorConsiderInflation.YES}`
        ),
        value: GermanPensionCalculatorConsiderInflation.YES,
      },
      {
        active:
          this.state.pensionConfig.formValues.bindValues.considerInflation ===
          GermanPensionCalculatorConsiderInflation.NO,
        text: this.icTranslateService.instant(
          this.brLabels[
            `gc-pension-calculator-consider-inflation-${GermanPensionCalculatorConsiderInflation.NO}`
          ],
          `gc-pension-calculator-consider-inflation-${GermanPensionCalculatorConsiderInflation.NO}`
        ),
        value: GermanPensionCalculatorConsiderInflation.NO,
      },
    ];
  }
  /**
   * Create result summary with row list.
   * @param calculatorType calculator type like one time/savings/withdrawl
   * @param summary result summary object
   */
  protected createResultSummaryData(
    calculatorType: GermanCalculatorType,
    summary: GermanCalculatorResultSummary
  ): GermanCalculatorSummaryData[] {
    const summaryData: GermanCalculatorSummaryData[] = [];
    let legendCount = 0; // Counter variable to store number of legend color used in each iteration.

    SUMMARY_ROW_LIST[calculatorType].forEach((row) => {
      switch (row) {
        case 'investmentPeriod': {
          summaryData.push({
            label: this.icTranslateService.instant(
              this.brLabels['gc-investment-period'],
              'gc-investment-period'
            ),
            value: this.checkAndAssignRowCalculatedValue(row, summary),
            valueType: GermanCalculatorSummaryDataValueType.PERIOD,
            showLegend: false,
          });
          break;
        }
        case 'interestRate': {
          summaryData.push({
            label: this.icTranslateService.instant(
              this.brLabels['gc-interest-rate'],
              'gc-interest-rate'
            ),
            value: this.checkAndAssignRowCalculatedValue(row, summary),
            valueType: GermanCalculatorSummaryDataValueType.RATE,
            showLegend: false,
          });
          break;
        }
        case 'finalBalance': {
          summaryData.push({
            label: this.icTranslateService.instant(
              this.brLabels['gc-final-capital'],
              'gc-final-capital'
            ),
            value: this.checkAndAssignRowCalculatedValue(row, summary),
            valueType: GermanCalculatorSummaryDataValueType.AMOUNT,
            showLegend: false,
            currencySymbol: this.getCurrencySymbol(),
          });
          break;
        }
        case 'initialBalance': {
          summaryData.push({
            label: this.icTranslateService.instant(
              this.brLabels['gc-initial-contribution'],
              'gc-initial-contribution'
            ),
            value: this.checkAndAssignRowCalculatedValue(row, summary),
            valueType: GermanCalculatorSummaryDataValueType.AMOUNT,
            showLegend: true,
            legendColor: this.highchartsThemeService.getPointColor(
              SUMMARY_LEGEND_ROW_COUNT[calculatorType] - legendCount - 1,
              SUMMARY_LEGEND_ROW_COUNT[calculatorType]
            ),
            currencySymbol: this.getCurrencySymbol(),
          });
          legendCount++;
          break;
        }
        case 'interestIncome': {
          summaryData.push({
            label: this.icTranslateService.instant(
              this.brLabels['gc-interest-income'],
              'gc-interest-income'
            ),
            value: this.checkAndAssignRowCalculatedValue(row, summary),
            valueType: GermanCalculatorSummaryDataValueType.AMOUNT,
            showLegend: true,
            legendColor: this.highchartsThemeService.getPointColor(
              SUMMARY_LEGEND_ROW_COUNT[calculatorType] - legendCount - 1,
              SUMMARY_LEGEND_ROW_COUNT[calculatorType]
            ),
            currencySymbol: this.getCurrencySymbol(),
          });
          legendCount++;
          break;
        }
        case 'installmentAmount': {
          summaryData.push({
            label: this.icTranslateService.instant(
              this.brLabels['gc-installment-amount'],
              'gc-installment-amount'
            ),
            value: this.checkAndAssignRowCalculatedValue(row, summary),
            valueType: GermanCalculatorSummaryDataValueType.AMOUNT,
            showLegend: false,
            currencySymbol: this.getCurrencySymbol(),
          });
          break;
        }
        case 'totalDeposits': {
          summaryData.push({
            label: this.icTranslateService.instant(
              this.brLabels['gc-total-deposits'],
              'gc-total-deposits'
            ),
            value: this.checkAndAssignRowCalculatedValue(row, summary),
            valueType: GermanCalculatorSummaryDataValueType.AMOUNT,
            showLegend:
              calculatorType === GermanCalculatorType.WITHDRAWL_PLAN
                ? false
                : true,
            legendColor:
              calculatorType === GermanCalculatorType.WITHDRAWL_PLAN
                ? ''
                : this.highchartsThemeService.getPointColor(
                    SUMMARY_LEGEND_ROW_COUNT[calculatorType] - legendCount - 1,
                    SUMMARY_LEGEND_ROW_COUNT[calculatorType]
                  ),
            currencySymbol: this.getCurrencySymbol(),
          });
          if (calculatorType !== GermanCalculatorType.WITHDRAWL_PLAN) {
            legendCount++;
          }
          break;
        }
        case 'withdrawlAmount': {
          summaryData.push({
            label: this.icTranslateService.instant(
              this.brLabels['gc-withdrawl-amount'],
              'gc-withdrawl-amount'
            ),
            value: this.checkAndAssignRowCalculatedValue(row, summary),
            valueType: GermanCalculatorSummaryDataValueType.AMOUNT,
            showLegend: false,
            currencySymbol: this.getCurrencySymbol(),
          });
          break;
        }
      }
    });

    return summaryData;
  }

  /**
   * Check the row and assign the value accordingly.
   * @param row row name
   * @param summary result summary object
   */
  private checkAndAssignRowCalculatedValue(
    row: keyof GermanCalculatorResultSummary,
    summary: GermanCalculatorResultSummary
  ): string {
    switch (row) {
      case 'investmentPeriod': {
        if (Number(summary.investmentPeriod.days) > 0) {
          const months = Math.ceil(Number(summary.investmentPeriod.days) / 30);

          if (months === 12) {
            summary.investmentPeriod.years = String(
              Number(summary.investmentPeriod.years) + 1
            );
          } else {
            summary.investmentPeriod.months = String(months);
          }
        }

        return Number(summary.investmentPeriod.years) > 1
          ? `${summary.investmentPeriod.years} ${this.yearsLabel}`
          : `${summary.investmentPeriod.years} ${this.yearLabel}`;
      }
      default: {
        return summary[row];
      }
    }
  }

  /**
   * Create chart with series.
   * @param calculatorType calculator type like one time/savings/withdrawl
   * @param periods result period list
   * @param summaryInitialBalance initial balance value in result summary
   * @param summaryInterestIncome interst income value in result summary
   * @param summaryFinalBalance final balance value in result summary
   */
  protected createResultChartData(
    calculatorType: GermanCalculatorType,
    periods: GermanCalculatorResultPeriod[],
    summaryInitialBalance: string,
    summaryInterestIncome?: string,
    summaryFinalBalance?: string
  ): GermanCalculatorChartData {
    const initialBalance = Number(summaryInitialBalance);
    const xAxisCategories: string[] = [];
    const stackCategory1ValueList: number[] = [];
    const stackCategory2ValueList: number[] = [];
    const stackCategory3ValueList: number[] = []; // for savings plan - initial contribution list.
    const series: SeriesOptionsType[] = [];

    // Counter variable for 'cumulativeInterest'.
    let cumulativeInterest = 0;

    // Counter variable for 'cumulativeDeposit'.
    let cumulativeDeposit = 0;

    periods.forEach((period) => {
      let stackCategory1Value: number;
      let stackCategory2Value: number;
      let xAxisValue: string;

      cumulativeInterest += Number(period.interestIncome);

      if (GermanCalculatorType.SAVINGS_PLAN) {
        cumulativeDeposit += Number(period.deposits);
      }

      // Assign stack categories.
      switch (calculatorType) {
        case GermanCalculatorType.ONE_TIME_INVESTMENT: {
          if (cumulativeInterest > 0) {
            stackCategory1Value = Number(period.finalBalance);
            stackCategory2Value = initialBalance;
          } else {
            stackCategory1Value = cumulativeInterest;
            stackCategory2Value = initialBalance;
          }
          break;
        }
        case GermanCalculatorType.SAVINGS_PLAN: {
          if (cumulativeInterest > 0) {
            stackCategory1Value =
              initialBalance + cumulativeDeposit + cumulativeInterest;
            stackCategory2Value = initialBalance + cumulativeDeposit;
          } else {
            stackCategory1Value = initialBalance + cumulativeDeposit;
            stackCategory2Value = cumulativeInterest;
          }
          break;
        }
        case GermanCalculatorType.WITHDRAWL_PLAN: {
          stackCategory1Value =
            Number(period.initialBalance) + Number(period.interestIncome);
          stackCategory2Value = Number(period.initialBalance);
          break;
        }
      }

      // Push the stack categories into respective list.
      stackCategory1ValueList.push(stackCategory1Value);
      stackCategory2ValueList.push(stackCategory2Value);

      if (GermanCalculatorType.SAVINGS_PLAN) {
        stackCategory3ValueList.push(initialBalance);
      }

      // Assign x-axis value for above stack categories.
      const years: string = period.elapsedTime.years;
      const days: string = period.elapsedTime.days;

      if (Number(days) > 0) {
        xAxisValue =
          years +
          ' ' +
          (Number(years) > 1 ? this.yearsLabel : this.yearLabel) +
          days +
          ' ' +
          (Number(days) > 1 ? this.daysLabel : this.dayLabel);
      } else {
        xAxisValue =
          years + ' ' + (Number(years) > 1 ? this.yearsLabel : this.yearLabel);
      }

      // Push the x-axis value in categories list.
      xAxisCategories.push(years);
    });

    if (
      calculatorType === GermanCalculatorType.WITHDRAWL_PLAN &&
      !periods.length
    ) {
      // Push the stack categories into respective list.
      stackCategory1ValueList.push(
        Number(summaryFinalBalance) + Number(summaryInterestIncome)
      );
      stackCategory2ValueList.push(Number(summaryFinalBalance));

      // Push the x-axis value in categories list.
      xAxisCategories.push(''); // Intentionally pushed empty. [Refer: aurelia code].
    }

    // Create final series with two stacks.
    switch (calculatorType) {
      case GermanCalculatorType.ONE_TIME_INVESTMENT: {
        series.push(
          {
            name: this.icTranslateService.instant(
              this.brLabels['gc-interest-rate'],
              'gc-interest-rate'
            ),
            type: 'column',
            data: stackCategory1ValueList,
            color: this.highchartsThemeService.getPointColor(
              0,
              SUMMARY_LEGEND_ROW_COUNT[calculatorType]
            ),
          },
          {
            name: this.icTranslateService.instant(
              this.brLabels['gc-initial-contribution'],
              'gc-initial-contribution'
            ),
            type: 'column',
            data: stackCategory2ValueList,
            color: this.highchartsThemeService.getPointColor(
              1,
              SUMMARY_LEGEND_ROW_COUNT[calculatorType]
            ),
          }
        );

        return {
          chartTitle: this.icTranslateService.instant(
            this.brLabels['gc-lump-sum-title'],
            'gc-lump-sum-title'
          ),
          proximalPlacementTop: 'GCOneTimeInvestmentChartProximalTop',
          proximalPlacementBottom: 'GCOneTimeInvestmentChartProximalBottom',
          caveatPlacement: 'GCOneTimeInvestmentChartCaveat',
          tooltip: this.icTranslateService.instant(
            this.brLabels['gc-lump-sum-chart-tooltip'],
            'gc-lump-sum-chart-tooltip'
          ),
          series,
          xAxisCategories,
          xAxisLabel: this.yearsLabel,
          yAxisLabel: this.getCurrencySymbol(),
        };
      }
      case GermanCalculatorType.SAVINGS_PLAN: {
        series.push(
          {
            name: this.icTranslateService.instant(
              this.brLabels['gc-interest-rate'],
              'gc-interest-rate'
            ),
            type: 'column',
            data: stackCategory1ValueList,
            color: this.highchartsThemeService.getPointColor(
              0,
              SUMMARY_LEGEND_ROW_COUNT[calculatorType]
            ),
          },
          {
            name: this.icTranslateService.instant(
              this.brLabels['gc-initial-contribution'],
              'gc-initial-contribution'
            ),
            type: 'column',
            data: stackCategory2ValueList,
            color: this.highchartsThemeService.getPointColor(
              1,
              SUMMARY_LEGEND_ROW_COUNT[calculatorType]
            ),
          },
          {
            name: this.icTranslateService.instant(
              this.brLabels['gc-initial-contribution'],
              'gc-initial-contribution'
            ),
            type: 'column',
            data: stackCategory3ValueList,
            color: this.highchartsThemeService.getPointColor(
              2,
              SUMMARY_LEGEND_ROW_COUNT[calculatorType]
            ),
          }
        );

        return {
          chartTitle: this.icTranslateService.instant(
            this.brLabels['gc-savings-plan-title'],
            'gc-savings-plan-title'
          ),
          proximalPlacementTop: 'GCSavingsPlanChartProximalTop',
          proximalPlacementBottom: 'GCSavingsPlanChartProximalBottom',
          caveatPlacement: 'GCSavingsPlanChartCaveat',
          tooltip: this.icTranslateService.instant(
            this.brLabels['gc-savings-plan-chart-tooltip'],
            'gc-savings-plan-chart-tooltip'
          ),
          series,
          xAxisCategories,
          xAxisLabel: this.yearsLabel,
          yAxisLabel: this.getCurrencySymbol(),
        };
      }
      case GermanCalculatorType.WITHDRAWL_PLAN: {
        series.push(
          {
            name: this.icTranslateService.instant(
              this.brLabels['gc-interest-rate'],
              'gc-interest-rate'
            ),
            type: 'column',
            data: stackCategory1ValueList,
            color: this.highchartsThemeService.getPointColor(
              0,
              SUMMARY_LEGEND_ROW_COUNT[calculatorType]
            ),
          },
          {
            name: this.icTranslateService.instant(
              this.brLabels['gc-initial-contribution'],
              'gc-initial-contribution'
            ),
            type: 'column',
            data: stackCategory2ValueList,
            color: this.highchartsThemeService.getPointColor(
              1,
              SUMMARY_LEGEND_ROW_COUNT[calculatorType]
            ),
          }
        );

        return {
          chartTitle: this.icTranslateService.instant(
            this.brLabels['gc-withdrawl-plan-title'],
            'gc-withdrawl-plan-title'
          ),
          proximalPlacementTop: 'GCWithdrawlPlanChartProximalTop',
          proximalPlacementBottom: 'GCWithdrawlPlanChartProximalBottom',
          caveatPlacement: 'GCWithdrawlPlanChartCaveat',
          tooltip: this.icTranslateService.instant(
            this.brLabels['gc-withdrawl-plan-chart-tooltip'],
            'gc-withdrawl-plan-chart-tooltip'
          ),
          series,
          xAxisCategories,
          xAxisLabel: this.yearsLabel,
          yAxisLabel: this.getCurrencySymbol(),
        };
      }
    }
  }

  /**
   * Create result table with header and body list.
   * @param calculatorType calculator type like one time/savings/withdrawl
   * @param periods result period list
   * @param summary result summary object
   */
  protected createResultTableData(
    calculatorType: GermanCalculatorType,
    periods: GermanCalculatorResultPeriod[],
    summary?: GermanCalculatorResultSummary
  ): GermanCalculatorTableData {
    const headers: string[] = [];
    const body: string[][] = [];
    // Counter variable for 'cumulativeInterest'.
    let cumulativeInterest = 0;

    // Counter variable for 'cumulativeDeposit'.
    let cumulativeDeposit = 0;

    // Step 1: Iterate table col list as per calculator type.
    //         And, Push header name appropriately.
    TABLE_COL_LIST[calculatorType].forEach((col) => {
      headers.push(
        this.icTranslateService.instant(
          this.brLabels[`gc-result-table-col-${col}`],
          `gc-result-table-col-${col}`
        )
      );
    });

    // Step 2: Iterate over periods response.
    periods.forEach((period) => {
      const row: string[] = []; // Consist list of column values for table grid.

      // Specific for one time investment.
      cumulativeInterest += Number(period.interestIncome);

      // Specific for savings plan.
      if (calculatorType === GermanCalculatorType.SAVINGS_PLAN) {
        cumulativeDeposit += Number(period.deposits);
      }

      // Step 3: Iterate table col list as per calculator type.
      //         Push the 'col' value from 'period'.
      TABLE_COL_LIST[calculatorType].forEach((col) => {
        row.push(
          this.checkAndAssignColumnCalculatedValue(
            col,
            period,
            cumulativeInterest,
            cumulativeDeposit
          )
        );
      });

      // Step 4: Push the 'row' value into 'body'.
      body.push(row);
    });

    // Step 5: In case of withdrawl plan calculator, check if, periods list is empty.
    //         If yes, assign first row at least.
    if (
      calculatorType === GermanCalculatorType.WITHDRAWL_PLAN &&
      !periods.length
    ) {
      const row: string[] = []; // Consist list of column values for table grid.

      TABLE_COL_LIST[calculatorType].forEach((col) => {
        row.push(
          this.checkAndAssignColumnCalculatedValue(col, {
            elapsedTime: {
              years: '1',
              days: '0',
            },
            initialBalance: summary.initialBalance,
            interestIncome: summary.interestIncome,
            withdrawls: summary.interestIncome,
            finalBalance: summary.finalBalance,
          })
        );
      });

      body.push(row);
    }

    // Configure pagination.
    this.pagedRecords.records = body;

    return {
      headers,
      body,
    };
  }

  /**
   * Check the column and assign the value accordingly.
   * @param col column name
   * @param period result period object
   * @param cumulativeInterest calculated cumulative interest
   * @param cumulativeDeposit calculated cumulative deposit
   */
  private checkAndAssignColumnCalculatedValue(
    col: keyof GermanCalculatorResultPeriodList,
    period: GermanCalculatorResultPeriod,
    cumulativeInterest?: number,
    cumulativeDeposit?: number
  ): string {
    switch (col) {
      case 'year': {
        const days: string = period.elapsedTime.days;
        const years: string = period.elapsedTime.years;

        if (days === '0') {
          return years;
        } else {
          const months = Math.ceil(Number(days) / 30);

          if (months === 12) {
            return String(Number(years) + 1);
          } else {
            return (
              years +
              (Number(years) > 1 ? this.yearsLabel : this.yearLabel) +
              ' ' +
              months +
              (Number(months) > 1 ? this.monthsLabel : this.monthLabel)
            );
          }
        }
      }
      case 'cumulativeInterest':
        return String(cumulativeInterest);
      case 'cumulativeDeposit':
        return String(cumulativeDeposit);
      default:
        return period[col as string];
    }
  }

  /* Return boolean for a form field value validity.
   * Checks negative value, beyond max limit, '-0', float value and 'e' value.
   * @param value form field current value
   * @param maxValue form field max value
   */
  isFormFieldValueInvalid(currentValue: string, maxValue: string): boolean {
    const value: string = String(currentValue); // Intentionally, typed to string to avoid string/number clash in future.

    if (!value) {
      return true;
    }

    if (
      Number(value) < 0 ||
      Number(value) > Number(maxValue) ||
      value === '-0' ||
      isNaN(Number(value))
    ) {
      return true;
    }

    return false;
  }

  /**
   * ******************** Pagination helper methods ********************
   */

  /**
   * Return total number of pages as observable.
   */
  getNumberOfPages$(): Observable<number> {
    return this.pagedRecords.numberOfPages$;
  }

  /**
   * Return current page records as observable.
   */
  getCurrentPage$(): Observable<string[][]> {
    return this.pagedRecords.currentPage$;
  }

  /**
   * Trigger page number selection accordingly.
   * @param n page number selected
   */
  goToPage(n: number): void {
    if (this.pagedRecords) {
      this.pagedRecords.pageNumber = n;
    }
  }
}
